import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type {
  BackupEintrag,
  WiederherstellungErgebnis,
  WiederherstellungVorschau,
} from '@mcikm-vote/shared';

import { apiAnfrage, apiRohantwort } from '../api/anfrage';
import type { ApiFehler } from '../api/anfrage';

/**
 * Anbindung der Backups (§28).
 *
 * Der Download läuft wie bei den Exporten über einen gewöhnlichen Verweis auf
 * {@link backupDateiPfad}, nicht über `fetch`: Der Browser kann das Speichern von Dateien besser
 * als jede Nachbildung im Anwendungscode.
 *
 * **Das Backup-Passwort verlässt die Oberfläche nur einmal** — beim Erzeugen. Es wird nirgends
 * gespeichert, weder hier noch auf dem Server (§28.1).
 */

const BACKUPS = '/api/v1/admin/backups';
const WIEDERHERSTELLUNG = '/api/v1/admin/wiederherstellung';

export const BACKUPS_SCHLUESSEL = ['admin', 'backups'] as const;

/** Pfad zum Herunterladen einer Backupdatei. Für `<a href>`; kein `fetch`. */
export function backupDateiPfad(dateiname: string): string {
  return `${BACKUPS}/${encodeURIComponent(dateiname)}/datei`;
}

/** Alle Backups und Sicherungsstände, neueste zuerst (§28.1, §28.3). */
export function useBackups(): UseQueryResult<BackupEintrag[], ApiFehler> {
  return useQuery<BackupEintrag[], ApiFehler>({
    queryKey: BACKUPS_SCHLUESSEL,
    queryFn: () => apiAnfrage<BackupEintrag[]>(BACKUPS),
  });
}

function useMitAktualisierung<TDaten, TVariablen>(
  fn: (variablen: TVariablen) => Promise<TDaten>,
): UseMutationResult<TDaten, ApiFehler, TVariablen> {
  const queryClient = useQueryClient();

  return useMutation<TDaten, ApiFehler, TVariablen>({
    mutationFn: fn,
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: BACKUPS_SCHLUESSEL });
    },
  });
}

/** Erzeugt ein manuelles Backup (§28.1). */
export function useBackupErzeugen(): UseMutationResult<BackupEintrag, ApiFehler, string> {
  return useMitAktualisierung((passwort) =>
    apiAnfrage<BackupEintrag>(BACKUPS, { methode: 'POST', koerper: { passwort } }),
  );
}

/** Löscht eine Backupdatei (§28.1). Sie ist danach unwiederbringlich fort. */
export function useBackupLoeschen(): UseMutationResult<void, ApiFehler, string> {
  return useMitAktualisierung((dateiname) =>
    apiAnfrage<void>(`${BACKUPS}/${encodeURIComponent(dateiname)}`, { methode: 'DELETE' }),
  );
}

/**
 * Bereitet eine Wiederherstellung vor (§28.2, Schritt 1).
 *
 * Bewusst über `fetch` mit `FormData` statt über {@link apiAnfrage}: Es ist die einzige Stelle der
 * Anwendung, die eine Datei **hinauf**lädt, und ein Multipart-Körper verträgt keinen
 * `Content-Type: application/json`. Der CSRF-Wert reist wie überall als Kopfzeile mit (§25.1).
 */
export async function bereiteWiederherstellungVor(angaben: {
  datei?: File;
  dateiname?: string;
  passwort?: string;
  sicherheitspasswort: string;
  alterSchluessel?: string;
}): Promise<WiederherstellungVorschau> {
  const koerper = new FormData();
  koerper.append('sicherheitspasswort', angaben.sicherheitspasswort);

  if (angaben.datei !== undefined) {
    koerper.append('datei', angaben.datei);
  }
  for (const name of ['dateiname', 'passwort', 'alterSchluessel'] as const) {
    const wert = angaben[name];
    if (wert !== undefined && wert !== '') {
      koerper.append(name, wert);
    }
  }

  return apiAnfrage<WiederherstellungVorschau>(`${WIEDERHERSTELLUNG}/vorbereiten`, {
    methode: 'POST',
    formular: koerper,
  });
}

/**
 * Lädt das Sicherheitsbackup herunter — der Nachweis nach §28.2.
 *
 * Hier **muss** es über `fetch` gehen und nicht über einen Verweis: Der Abruf ist ein `POST`, weil
 * er den Vorgang fortsetzt, und die Oberfläche muss wissen, wann er gelungen ist. Die Datei wird
 * anschließend über einen Objekt-URL gespeichert.
 */
export async function ladeSicherheitsbackup(vorgangId: string, dateiname: string): Promise<void> {
  const antwort = await apiRohantwort(`${WIEDERHERSTELLUNG}/${vorgangId}/sicherheitsbackup`, {
    methode: 'POST',
  });

  const blob = await antwort.blob();
  const url = URL.createObjectURL(blob);

  try {
    const verweis = document.createElement('a');
    verweis.href = url;
    verweis.download = dateiname;
    verweis.click();
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Führt die Wiederherstellung aus (§28.2, Schritt 3). */
export function fuehreWiederherstellungAus(vorgangId: string): Promise<WiederherstellungErgebnis> {
  return apiAnfrage<WiederherstellungErgebnis>(`${WIEDERHERSTELLUNG}/${vorgangId}/ausfuehren`, {
    methode: 'POST',
  });
}

/** Bricht einen vorbereiteten Vorgang ab. Das Sicherheitsbackup bleibt erhalten. */
export function brichWiederherstellungAb(vorgangId: string): Promise<void> {
  return apiAnfrage<void>(`${WIEDERHERSTELLUNG}/${vorgangId}`, { methode: 'DELETE' });
}
